import { useEffect, useRef, useState } from 'react'
import { heicToJpeg, looksLikeHeic } from '../lib/heic'
import { useT } from '../i18n'

interface Props {
  /** The raw file the user picked. */
  file: File
  onCancel: () => void
  /** Receives the cropped square image, ready for uploadProfileAvatar. */
  onConfirm: (cropped: File) => void
}

/** Output edge in pixels. Matches uploadProfileAvatar's 600 target. */
const OUT = 600
const MAX_ZOOM = 4

/**
 * Circle-crop preview shown between picking a photo and saving it.
 * Drag to position, pinch or use the slider to zoom, and the circle
 * shows exactly what the avatar will look like. Confirm renders the
 * visible circle to a square canvas, so the automatic centre-crop in
 * uploadProfileAvatar becomes a no-op and the pipeline is unchanged.
 *
 * Gestures are pointer events: one pointer pans, two pinch. The image
 * is clamped so it always covers the circle, which means no white
 * wedges can ever appear in the final avatar.
 */
export function AvatarCropOverlay({ file, onCancel, onConfirm }: Props) {
  const t = useT()
  // Diameter of the circular crop window, measured per mount. Height
  // matters as much as width: in a short viewport (landscape, or a
  // small phone once browser chrome is taken out) the flex column
  // shrinks this box, and every crop figure below is derived from VIEW
  // rather than the box's real size, so a shrunk box silently crops
  // more than the circle shows. CHROME is the rest of the column:
  // title, hint, slider and buttons, with their margins.
  const [VIEW] = useState(() => {
    const CHROME = 210
    return Math.max(160, Math.min(300, Math.floor(window.innerWidth - 80),
      Math.floor(window.innerHeight - CHROME)))
  })
  const [decoded, setDecoded] = useState<File | null>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [converting, setConverting] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const pinchDist = useRef<number | null>(null)
  /** One recovery attempt only, so a bad convert cannot loop. */
  const triedRecovery = useRef(false)

  /** The original photo, or the JPEG we decoded it into. */
  const source = decoded ?? file

  // The object URL is created and revoked by the same effect. Creating
  // it alongside state instead means the cleanup revokes a URL that is
  // still on screen the moment the effect runs twice, which is exactly
  // what StrictMode does in development: the photo then loads as 0x0
  // and every upload looks broken.
  useEffect(() => {
    const objectUrl = URL.createObjectURL(source)
    setSrc(objectUrl)
    setDims(null)
    return () => URL.revokeObjectURL(objectUrl)
  }, [source])

  /**
   * The browser could not decode what the user picked. If it is HEIC,
   * decode it here and carry on with the JPEG; the swap replaces the
   * image source, so every measurement below is taken from the decoded
   * photo and the crop stays honest.
   */
  async function recover() {
    if (triedRecovery.current) { setFailed(true); return }
    triedRecovery.current = true
    if (!(await looksLikeHeic(file))) { setFailed(true); return }
    setConverting(true)
    try {
      setDecoded(await heicToJpeg(file))
    } catch {
      setFailed(true)
    } finally {
      setConverting(false)
    }
  }

  // Cover scale: the image's short side exactly fills the circle at
  // zoom 1. Everything else is relative to this.
  const baseScale = dims ? VIEW / Math.min(dims.w, dims.h) : 1
  const scale = baseScale * zoom

  function clampOffset(o: { x: number; y: number }, z = zoom) {
    if (!dims) return o
    const s = baseScale * z
    const maxX = Math.max(0, (dims.w * s - VIEW) / 2)
    const maxY = Math.max(0, (dims.h * s - VIEW) / 2)
    return {
      x: Math.min(maxX, Math.max(-maxX, o.x)),
      y: Math.min(maxY, Math.max(-maxY, o.y)),
    }
  }

  function setZoomClamped(z: number) {
    const nz = Math.min(MAX_ZOOM, Math.max(1, z))
    setZoom(nz)
    setOffset(o => clampOffset(o, nz))
  }

  function onPointerDown(e: React.PointerEvent) {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      pinchDist.current = Math.hypot(a.x - b.x, a.y - b.y)
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const prev = pointers.current.get(e.pointerId)
    if (!prev) return
    const cur = { x: e.clientX, y: e.clientY }
    pointers.current.set(e.pointerId, cur)

    if (pointers.current.size === 2 && pinchDist.current) {
      const [a, b] = [...pointers.current.values()]
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      setZoomClamped(zoom * (d / pinchDist.current))
      pinchDist.current = d
    } else if (pointers.current.size === 1) {
      setOffset(o => clampOffset({ x: o.x + cur.x - prev.x, y: o.y + cur.y - prev.y }))
    }
  }

  function onPointerEnd(e: React.PointerEvent) {
    pointers.current.delete(e.pointerId)
    if (pointers.current.size < 2) pinchDist.current = null
  }

  async function confirm() {
    const img = imgRef.current
    if (!img || !dims) return
    setBusy(true)
    try {
      // The circle's centre in natural-image pixels, derived from the
      // display transform, then a VIEW-sized square around it.
      const srcSize = VIEW / scale
      const srcCx = dims.w / 2 - offset.x / scale
      const srcCy = dims.h / 2 - offset.y / scale
      const srcX = Math.max(0, Math.min(dims.w - srcSize, srcCx - srcSize / 2))
      const srcY = Math.max(0, Math.min(dims.h - srcSize, srcCy - srcSize / 2))

      const canvas = document.createElement('canvas')
      canvas.width = OUT
      canvas.height = OUT
      const ctx = canvas.getContext('2d')!
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, OUT, OUT)

      const blob = await new Promise<Blob | null>(res =>
        canvas.toBlob(res, 'image/jpeg', 0.92))
      if (!blob) throw new Error('Could not read that image. Try a different photo.')
      onConfirm(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center
                    justify-center px-6 py-4 overflow-y-auto"
         style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <p className="text-white font-semibold mb-1">{t('Position your photo')}</p>
      <p className="text-white/60 text-xs mb-5">
        {failed ? t('This one could not be opened.')
          : converting ? t('Opening your Apple photo. This takes a moment.')
          : t('Drag to move. Pinch or slide to zoom.')}
      </p>

      <div
        className="rounded-full overflow-hidden ring-2 ring-white/80 shadow-2xl
                   touch-none select-none relative bg-black shrink-0"
        style={{ width: VIEW, height: VIEW }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        {/* Sized and positioned in plain pixels rather than transforms:
            CSS translate percentages measure the unscaled element, which
            silently drifts the centring once zoomed. With computed
            left/top the display math and the crop math share one
            coordinate system by construction. */}
        <img
          ref={imgRef}
          src={src ?? undefined}
          alt=""
          draggable={false}
          onLoad={e => {
            const el = e.currentTarget
            // Ignore anything reported against a source we have already
            // moved on from: a revoked URL can still deliver a late
            // event, and acting on it would spend the one retry below.
            if (el.src !== src) return
            // A decode that fails silently still fires load on some
            // engines, with zero natural size. Treat that as a failure
            // to decode, not as dimensions.
            if (!el.naturalWidth || !el.naturalHeight) { void recover(); return }
            setDims({ w: el.naturalWidth, h: el.naturalHeight })
          }}
          onError={e => { if (e.currentTarget.src === src) void recover() }}
          className="absolute max-w-none pointer-events-none"
          style={dims ? {
            width: dims.w * scale,
            height: dims.h * scale,
            left: VIEW / 2 - (dims.w * scale) / 2 + offset.x,
            top:  VIEW / 2 - (dims.h * scale) / 2 + offset.y,
          } : { opacity: 0 }}
        />
        {(failed || converting) && (
          <div className="absolute inset-0 flex items-center justify-center px-7 text-center">
            <p className="text-white/80 text-xs leading-relaxed">
              {converting
                ? 'Converting…'
                : t('This photo could not be opened. Pick another one, or save this one as a JPEG first.')}
            </p>
          </div>
        )}
      </div>

      <input
        type="range"
        min={1}
        max={MAX_ZOOM}
        step={0.01}
        value={zoom}
        onChange={e => setZoomClamped(Number(e.target.value))}
        aria-label={t('Zoom')}
        disabled={failed || !dims}
        className="w-full max-w-[300px] mt-6 accent-white disabled:opacity-40"
      />

      <div className="flex gap-3 mt-6 w-full max-w-[300px]">
        {failed ? (
          // A disabled Use photo next to a dead circle reads as a broken
          // screen. One live button gives the user somewhere to go.
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 rounded-2xl font-semibold bg-white text-brand-text
                       active:scale-95 transition-transform"
          >{t('Pick another photo')}</button>
        ) : (
          <>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="flex-1 py-3 rounded-2xl font-semibold text-white/90
                         border border-white/40 active:scale-95 transition-transform"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={busy || !dims}
              className="flex-1 py-3 rounded-2xl font-semibold bg-white text-brand-text
                         active:scale-95 transition-transform disabled:opacity-60"
            >
              {busy ? 'Cropping…' : t('Use photo')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
